import type {NormalizedCacheObject} from '@apollo/client';
import {type ApolloClientProxy, type BaseHttpCodes, EntityBaseGqlService} from '@igp/shared/api';
import type {KeycloakLog, Mutation, Query, QueryGetKeycloakLogsArgs, Subscription} from '@igp/shared/contract/pam';
import type {IMessageLocalizer} from '@igp/shared/localization';
import {map} from '@igp/shared/otel';
import type {EntityFetchRequestPayload, EntityFetchServiceResponsePayload} from '@igp/shared/redux/entity';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {KeycloakLogRequestBuilder} from './requestBuilder';
import type {KeycloakLogEntityType, KeycloakLogFilterKeys, KeycloakLogQueryFields} from './types';

@injectable()
export class KeycloakLogService extends EntityBaseGqlService<
    KeycloakLogEntityType,
    Query,
    QueryGetKeycloakLogsArgs,
    KeycloakLogQueryFields,
    Mutation,
    Subscription,
    BaseHttpCodes,
    KeycloakLogFilterKeys
> {
    protected readonly _requestBuilder: KeycloakLogRequestBuilder;

    constructor(
        @inject(ServiceTypes.ApolloClientIGP) client: ApolloClientProxy<NormalizedCacheObject>,
        @inject(ServiceTypes.MessageLocalizer) messageLocalizer: IMessageLocalizer
    ) {
        const builder = new KeycloakLogRequestBuilder();
        super(client, builder, messageLocalizer);
        this._requestBuilder = builder;
    }

    get(
        requestPayload: EntityFetchRequestPayload<KeycloakLogEntityType, KeycloakLogQueryFields>
    ): Observable<EntityFetchServiceResponsePayload<KeycloakLogEntityType, KeycloakLog>> {
        return this.fetchData(requestPayload).pipe(
            map(response => ({
                ...response,
                requestPayload,
                responsePayload: {
                    items: response.responsePayload?.getKeycloakLogs?.items ?? [],
                    total: response.responsePayload?.getKeycloakLogs?.items?.length ?? 0,
                },
            }))
        );
    }

    protected getErrorSeverity(_code: BaseHttpCodes): number {
        return 0;
    }
}
